/**
 * Control Activities Slice
 * 
 * Manages control activities data with caching and draft support
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ControlActivity, Auditor } from '../../services/controlActivitiesService';
import { resetReduxState } from '../actions';

// Draft Control Activity interface for creating new controls
// Note: cycleId, processId, controlCode are READ-ONLY after draft is created
export interface DraftControlActivity {
  // Prerequisites (READ-ONLY - set once when draft is created)
  cycleId: string;
  cycleCode: string;
  cycleName: string;
  processId: string;
  processCode: string;
  processName: string;
  riskAssessmentId: string;
  processOwnerId: string;
  processOwnerName: string;

  // Identity (READ-ONLY - auto-generated when draft is created)
  controlCode: string;
  
  // Description (6 Ws)
  what: string;
  when: string; // Frequency dropdown value
  who: string;
  why: string;
  how: string;
  evidence: string;
  
  // Classification
  category: 'financial' | 'operational' | 'regulatory' | null;
  controlType: 'preventative' | 'detective' | 'other' | null;
  isKeyControl: boolean | null;
  
  // Ownership
  controlOwner: string;
  objectiveIds: string[];
  applicationIds: string[];
  
  // Testing
  inTestingScope: boolean;
  testPlan: string;
  walkthroughResult: string;
  isDesignEffective: boolean | null; // null = Not Started
  operatingEffectiveness: boolean | null; // null = Not Started
  samplesTotal: number | null;
  samplesTested: number | null;
  interimCompleted: 'notStarted' | 'wip' | 'yes';
  finalCompleted: 'notStarted' | 'wip' | 'completed';
  documentIds: string[];
  
  // GAP related
  isGapCompensating: boolean;
  
  // Admin
  auditorId: string | null;
  budgetHours: number | null;
}

export interface ControlActivitiesState {
  // All control activities for current annual report
  controlActivities: ControlActivity[];
  totalCount: number;
  lastFetchedReportId: string | null;
  
  // Pagination state
  currentPage: number;
  pageSize: number;
  
  // Draft state for creating new control (null = no draft)
  draftControl: DraftControlActivity | null;
  
  // Auditors list (cached per company)
  auditors: Auditor[];
  auditorsLastFetchedCompanyId: string | null;
}

const initialState: ControlActivitiesState = {
  controlActivities: [],
  totalCount: 0,
  lastFetchedReportId: null,
  currentPage: 1,
  pageSize: 10,
  draftControl: null,
  auditors: [],
  auditorsLastFetchedCompanyId: null,
};

const controlActivitiesSlice = createSlice({
  name: 'controlActivities',
  initialState,
  reducers: {
    // Set control activities data
    setControlActivities: (
      state,
      action: PayloadAction<{
        data: ControlActivity[];
        totalCount: number;
        reportId: string;
      }>
    ) => {
      state.controlActivities = action.payload.data;
      state.totalCount = action.payload.totalCount;
      state.lastFetchedReportId = action.payload.reportId;
    },

    // Update pagination
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to first page when changing page size
    },

    // Clear all control activities data
    clearControlActivitiesData: (state) => {
      state.controlActivities = [];
      state.totalCount = 0;
      state.lastFetchedReportId = null;
      state.currentPage = 1;
    },

    // ==================== DRAFT ACTION ====================
    
    /**
     * Set or update the draft control item
     * - Pass a DraftControlActivity to create/update draft
     * - Pass null to clear draft
     * 
     * Note: Cycle, Process, and ControlCode are READ-ONLY after draft is created
     * They should only be set once when creating the initial draft
     */
    setDraftItem: (state, action: PayloadAction<DraftControlActivity | null>) => {
      state.draftControl = action.payload;
    },

    // ==================== AUDITORS ACTIONS ====================
    
    /**
     * Set auditors list (cached per company)
     */
    setAuditors: (
      state,
      action: PayloadAction<{
        auditors: Auditor[];
        companyId: string;
      }>
    ) => {
      state.auditors = action.payload.auditors;
      state.auditorsLastFetchedCompanyId = action.payload.companyId;
    },

    /**
     * Clear auditors cache
     */
    clearAuditors: (state) => {
      state.auditors = [];
      state.auditorsLastFetchedCompanyId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetReduxState, () => initialState);
  },
});

export const {
  setControlActivities,
  setCurrentPage,
  setPageSize,
  clearControlActivitiesData,
  setDraftItem,
  setAuditors,
  clearAuditors,
} = controlActivitiesSlice.actions;

export default controlActivitiesSlice.reducer;

